(ns quuppa.qsm.planner.events
  (:require [re-frame.core :as rf]
            [quuppa.peweb.query :as q]
            [ajax.core :as ajax]
            [day8.re-frame.http-fx]
            [cljs.reader :as reader]
            [quuppa.qsm.planner.utils :as planner-utils]
            [goog.string :as gstr]
            [goog.string.format]))

(def plans-query (q/create-query :planner/list-plans []))
(def plan-query (q/create-query :planner/get-plan [:id]))

(rf/reg-event-db :planner/plan-updated
                 (fn [db [_ _result :as event]]
                   ;; Update the WebSocket query cache so query subscribers see fresh data.
                   ;; We deliberately do NOT overwrite db[:site-plan plan-id :imdf] here because
                   ;; load-imdf-siteplan-edn omits :rectangleM from background images, and
                   ;; overwriting :imdf would strip the CSS sizing used for scale-calibrated display.
                   (-> db
                       ((q/update-event-fn plan-query (fn [_result qpe] qpe)) event))))

(defn extract-level
  ([imdf-plan level-id]
   ;; Search all buildings' levels for the level-id
   (let [buildings (vals (get-in imdf-plan [:venue :buildings]))]
     (some (fn [b]
             (some (fn [lvl]
                     (when (= (str level-id) (:id lvl)) lvl))
                   (vals (:levels b))))
           buildings)))
  ([imdf-plan building-id level-id]
   (get-in imdf-plan [:venue :buildings (str building-id) :levels (str level-id)])))

(defn transform-background-images
  "Transform background images to set :ppx from DB's :scale value.
   Backend returns :scale as pixels-per-meter.
   Frontend needs :ppx (pixels-per-meter) - just copy the scale value."
  [imdf-plan]
  (update-in imdf-plan [:venue :buildings]
             (fn [buildings]
               (into {}
                     (map (fn [[building-id building]]
                            [building-id
                             (update building :levels
                                     (fn [levels]
                                       (into {}
                                             (map (fn [[level-id level]]
                                                    [level-id
                                                     (update level :siteresource-backgroundimages
                                                             (fn [bg-images]
                                                               (let [sorted (sort-by (comp #(or (:sort-order %) 0) second) bg-images)]
                                                                 (into {}
                                                                       (map-indexed (fn [i [bg-id bg-img]]
                                                                                      [bg-id
                                                                                       (cond-> bg-img
                                                                                               true (assoc :sort-order i)
                                                                                               (:scale bg-img) (assoc :ppx (:scale bg-img)))])
                                                                                    sorted)))))])
                                                  levels))))])
                          buildings)))))

(defn convert-plan-meters->pixels
  "Convert all stored meter coordinates to pixel coordinates for rendering.
   Each level's ppm is taken from its first background image's :scale.
   Called after loading a plan from DB (which stores meters)."
  [imdf-plan]
  (update-in imdf-plan [:venue :buildings]
             (fn [buildings]
               (into {}
                     (map (fn [[bid building]]
                            [bid (update building :levels
                                         (fn [levels]
                                           (into {}
                                                 (map (fn [[lid level]]
                                                        (let [ppm (or (some-> level :siteresource-backgroundimages vals first :scale)
                                                                      100.0)
                                                              ;; imageHeightPx from DB load or after scale calibration;
                                                              ;; used to flip y-axis: meter coords have y=0 at bottom (growing up),
                                                              ;; pixel/display coords have y=0 at top (growing down).
                                                              img-height-px (or (some-> level :siteresource-backgroundimages vals first :imageHeightPx)
                                                                                (some-> level :siteresource-backgroundimages vals first :image-height-px)
                                                                                0)
                                                              pt->px  (fn [[x y]] [(* x ppm) (if (pos? img-height-px) (- img-height-px (* y ppm)) (* y ppm))])
                                                              pts->px (fn [pts] (mapv pt->px pts))]
                                                          [lid (cond-> level
                                                                 ;; Convert level geometry rings (Polygon) from meters to pixels
                                                                 (get-in level [:geometry :coordinates])
                                                                 (update-in [:geometry :coordinates]
                                                                            (fn [rings] (mapv pts->px rings)))
                                                                 (seq (:units level))
                                                                 (update :units
                                                                         (fn [units]
                                                                           (into {} (map (fn [[uid u]]
                                                                                           [uid (update u :points pts->px)])
                                                                                         units))))
                                                                 (seq (:openings level))
                                                                 (update :openings
                                                                         (fn [openings]
                                                                           (into {} (map (fn [[oid o]]
                                                                                           [oid (update o :points pts->px)])
                                                                                         openings))))
                                                                 (seq (:geofences level))
                                                                 (update :geofences
                                                                         (fn [geofences]
                                                                           (into {} (map (fn [[gid g]]
                                                                                           [gid (update g :points pts->px)])
                                                                                         geofences))))
                                                                 (seq (:locators level))
                                                                 (update :locators
                                                                         (fn [locators]
                                                                           (into {} (map (fn [[locid loc]]
                                                                                           [locid (assoc loc
                                                                                                         :x (* (:x loc) ppm)
                                                                                                         :y (if (pos? img-height-px)
                                                                                                              (- img-height-px (* (:y loc) ppm))
                                                                                                              (* (:y loc) ppm)))])
                                                                                         locators))))
                                                                 (seq (:routes level))
                                                                 (update :routes
                                                                         (fn [routes]
                                                                           (into {} (map (fn [[rid r]]
                                                                                           [rid (update r :points pts->px)])
                                                                                         routes)))))]))
                                                      levels))))])
                          buildings)))))

(rf/reg-event-fx ::plan-loaded
  (fn [{:keys [db]} [_ plan-id]]
    (let [raw-result (:result (q/get-result db plan-query {:id plan-id}))
          ; FIXME we should remove the result from app-db and
          ; transform the data so it's usable in the planner
          ; and invoke loading floorplan images

          _ (println "::plan-loaded" raw-result)
          ;; Server currently returns EDN string; parse defensively.
          raw-plan (cond
                     (string? raw-result) (reader/read-string raw-result)
                     (map? raw-result) raw-result
                     :else {})
          ;; Transform background images to have both :ppx and :scale
          _ (js/console.log "About to transform background images")
          imdf-plan (-> raw-plan
                        transform-background-images
                        convert-plan-meters->pixels)
          _ (js/console.log "After transformation, imdf-plan:" (clj->js imdf-plan))
          selected-building-id (-> db :quuppa.routing/match :parameters :query :building-id)
          selected-level-id (-> db :quuppa.routing/match :parameters :query :level-id)
          selected-background-id (-> db :quuppa.routing/match :parameters :query :background-id)
          level (cond
                  (and selected-building-id selected-level-id)
                  (extract-level imdf-plan selected-building-id selected-level-id)
                  selected-level-id
                  (extract-level imdf-plan selected-level-id)
                  :else nil)
          ;; Pick background image: prefer query param background-id, else first available
          bg-map (:siteresource-backgroundimages level)
          chosen-bg-id (or (some-> selected-background-id str)
                           (some-> bg-map keys first))
          chosen-bg (when chosen-bg-id (get bg-map chosen-bg-id))
          floorplan-url (or (when-let [asset-id (:asset-id chosen-bg)]
                              ;; Endpoint expected to serve asset by id
                              (str "/site-plan/background-assets/" asset-id))
                            (get-in level [:floorplan :image_url]))
          ;; Minimal legacy nodes structure so planner UI doesn't break when expecting :nodes
          nodes {"root" {:type "Root"
                         :properties {:name (get-in imdf-plan [:site-plan :name])}
                         :children []}}]
      {:db (-> db
               (assoc-in [:site-plan plan-id] {:nodes nodes
                                               :imdf imdf-plan
                                               :selected-level-floorplan floorplan-url}))})))

(rf/reg-event-fx ::init-new-plan
  (fn [{:keys [db]} _]
    {:fx [[:dispatch [:nav/push-state :planner-new-plan]]]
     :db (assoc db :site-plan {:new {:nodes {"root" {:type "Root"
                                                     :properties {:name "Unnamed site plan"}
                                                     :children []}}
                                     :resources {}}})}))

(rf/reg-event-fx ::add-node
  (fn [{:keys [db]} [_ plan-id parent-id node-type]]
    (let [id (str (random-uuid))
          new-node {:type node-type
                    :properties {:id id
                                 :name ""}
                    :children []}]
      {:db (-> db
               (update-in [:site-plan plan-id :nodes parent-id] update :children conj id)
               (update-in [:site-plan plan-id :nodes] assoc id new-node))
       :fx [[:update-query [assoc :selected id]]]})))

(defn remove-node [nodes node-id]
  ;; Remove children of the node being removed
  (let [{:keys [children]} (get nodes node-id)]
    (->> (reduce remove-node
                 ;; Remove this node
                 (dissoc nodes node-id)
                 children)
         ;; Remove references to this node
         (map (fn [[id node]]
                [id (if (map? node)
                      (assoc node :children (vec (remove #(= % node-id) (:children node))))
                      node)]))
         (into (empty nodes)))))

(rf/reg-event-db ::remove-node
  (fn [db [_ plan-id node-id]]
    (-> db (update-in [:site-plan plan-id :nodes] remove-node node-id))))

(rf/reg-event-db ::assoc-property
  (fn [db [_ plan-id node-id property value]]
    (-> db
        (assoc-in [:site-plan plan-id :nodes node-id :properties property] value))))

(rf/reg-event-fx ::set-image-resource
  (fn [_ [_ plan-id node-id blob]]
    {:fx [[:http-xhrio {:method          :post
                        :uri             "/planner/files"
                        :body            (doto (js/FormData.)
                                           (.append "file" blob))
                        :on-success      [::image-uploaded plan-id node-id]
                        :on-failure      [::image-upload-error]
                        :response-format (ajax/json-response-format {:keywords? true})}]]}))

(rf/reg-event-fx
 ::set-scale-distance
 (fn [{:keys [db]} [_ {:keys [scale-points scale-distance img-width img-height asset]}]]
   (let [ppm        (or (planner-utils/pixels-per-meter scale-points scale-distance) 100)
         rectangle-m  (planner-utils/rectangle-m img-width img-height ppm)
         selected-building-id (-> db :quuppa.routing/match :parameters :query :building-id)
         selected-level-id (-> db :quuppa.routing/match :parameters :query :level-id)
         ;; Use background-id from the asset, not from URL params
         selected-background-id (or (:background-id asset)
                                    (-> db :quuppa.routing/match :parameters :query :background-id))
         ;; Fall back to :new for the new-plan route which has no :plan-id path param
         site-plan-id (or (-> db :quuppa.routing/match :parameters :path :plan-id) :new)]
     {:db (-> db
              ;; Store pixels-per-meter in both :ppx and :scale for consistency
              (assoc-in [:site-plan site-plan-id :imdf :venue :buildings selected-building-id :levels selected-level-id :siteresource-backgroundimages selected-background-id :ppx] ppm)
              (assoc-in [:site-plan site-plan-id :imdf :venue :buildings selected-building-id :levels selected-level-id :siteresource-backgroundimages selected-background-id :scale] ppm)
              (assoc-in [:site-plan site-plan-id :imdf :venue :buildings selected-building-id :levels selected-level-id :siteresource-backgroundimages selected-background-id :rectangleM] rectangle-m)
              ;; Store image height in pixels for y-axis coordinate convention (y grows up in meter space)
              (assoc-in [:site-plan site-plan-id :imdf :venue :buildings selected-building-id :levels selected-level-id :siteresource-backgroundimages selected-background-id :imageHeightPx] img-height))
      :fx [[:server-dispatch {:event      [:qsm/update-background-image-asset {:asset-id (:asset-id asset)
                                                                               :ppx ppm
                                                                               :rectangleM rectangle-m}]
                              :on-success :ignore
                              :on-error [::update-background-image-asset-error]}]]})))

(rf/reg-event-fx ::image-uploaded
  (fn [{:keys [db]} [_ plan-id node-id resp]]
    {:db (-> db
             (assoc-in [:site-plan plan-id :nodes node-id :properties :image] (:file_id resp)))}))

;; Distinct colors for simulation paths - easy to tell apart
(def route-colors
  ["#e41a1c"   ; red
   "#377eb8"   ; blue
   "#4daf4a"   ; green
   "#984ea3"   ; purple
   "#ff7f00"   ; orange
   "#a65628"   ; brown
   "#f781bf"   ; pink
   "#999999"   ; gray
   "#17becf"   ; cyan
   "#bcbd22"]) ; olive

;; Colors for geofences - using similar palette
(def geofence-colors
  ["#377eb8"   ; blue
   "#4daf4a"   ; green
   "#e41a1c"   ; red
   "#ff7f00"   ; orange
   "#984ea3"   ; purple
   "#17becf"   ; cyan
   "#a65628"   ; brown
   "#f781bf"   ; pink
   "#bcbd22"   ; olive
   "#999999"]) ; gray

;; Colors for units - using similar palette
(def unit-colors
  ["#4daf4a"   ; green
   "#ff7f00"   ; orange
   "#377eb8"   ; blue
   "#984ea3"   ; purple
   "#e41a1c"   ; red
   "#bcbd22"   ; olive
   "#17becf"   ; cyan
   "#f781bf"   ; pink
   "#a65628"   ; brown
   "#999999"]) ; gray

;; Add a new route to a level in the IMDF structure.
;; Sets route-id in URL so tree can select the new route.
;; initial-points should be provided based on the current image dimensions.
;; Automatically assigns a color based on the number of existing routes.
(rf/reg-event-fx ::add-route
  (fn [{:keys [db]} [_ plan-id building-id level-id route-name initial-points]]
    (let [route-id (str (random-uuid))
          existing-routes (get-in db [:site-plan plan-id :imdf :venue :buildings building-id :levels level-id :routes])
          route-index (count existing-routes)
          route-color (nth route-colors (mod route-index (count route-colors)))
          new-route {:id route-id
                     :name route-name
                     :points (or initial-points [])
                     :color route-color}]
      (js/console.log "EVENT add-route: route-id=" route-id "color=" route-color)
      {:db (assoc-in db [:site-plan plan-id :imdf :venue :buildings building-id :levels level-id :routes route-id] new-route)
       :fx [[:dispatch [:nav/update-query merge {:route-id route-id}]]]})))

(rf/reg-event-fx ::update-route-points
  (fn [{:keys [db]} [_ plan-id building-id level-id route-id points]]
    {:db (assoc-in db [:site-plan plan-id :imdf :venue :buildings building-id :levels level-id :routes route-id :points] points)}))

(rf/reg-event-db ::update-route-props
  "Merge arbitrary property map into a route (used by the edit panel)."
  (fn [db [_ plan-id building-id level-id route-id props]]
    (update-in db [:site-plan plan-id :imdf :venue :buildings building-id :levels level-id :routes route-id]
               merge props)))

;; Delete a route from a level in the IMDF structure.
(rf/reg-event-fx ::delete-route
  (fn [{:keys [db]} [_ plan-id building-id level-id route-id]]
    {:db (update-in db [:site-plan plan-id :imdf :venue :buildings building-id :levels level-id :routes] dissoc route-id)
     :fx [[:dispatch [:nav/update-query dissoc :route-id]]]}))

;; Delete a background image from a level in the IMDF structure.
(rf/reg-event-fx ::delete-background
                 (fn [{:keys [db]} [_ plan-id building-id level-id background-id]]
                   {:db (update-in db [:site-plan plan-id :imdf :venue :buildings building-id
                                       :levels level-id :siteresource-backgroundimages]
                                   dissoc background-id)
                    :fx [[:dispatch [:nav/update-query dissoc :background-id]]
                         [:server-dispatch {:event      [:qsm/delete-background-image
                                                         {:background-id (uuid background-id)}]
                                            :on-success :ignore
                                            :on-error   [::delete-background-error]}]]}))

(rf/reg-event-fx ::delete-background-error
                 (fn [_ [_ error]]
                   (js/console.error "Delete background failed:" error)
                   {:fx [[:dispatch [:notifications/notify
                                     {:type :error
                                      :message "Failed to delete background image"}]]]}))

(rf/reg-event-fx ::add-background-to-level
                 (fn [_ [_ plan-id building-id level-id js-file on-success!]]
                   (let [form-data (js/FormData.)]
                     (.append form-data "file"         js-file)
                     (.append form-data "site-plan-id" (str plan-id))
                     (.append form-data "level-id"     (str level-id))
                     {:fx [[:http-xhrio {:method          :post
                                         :uri             "/site-plan/floorplan"
                                         :body            form-data
                                         :response-format (ajax/json-response-format {:keywords? true})
                                         :on-success      [::add-background-success plan-id building-id level-id on-success!]
                                         :on-failure      [::add-background-error]}]]})))

(rf/reg-event-fx ::add-background-success
                 (fn [_ [_ plan-id building-id level-id on-success! response]]
                   (let [background-id (str (:background-id response))]
                     (when on-success! (on-success!))
                     {:fx [[::q/execute [plan-query {:id plan-id}
                                         {:on-success [::plan-loaded-then-navigate
                                                       plan-id building-id level-id background-id]}]]
                           [:dispatch [:notifications/notify-success {:message "Background image added"}]]]})))

(rf/reg-event-fx ::plan-loaded-then-navigate
                 (fn [_ [_ plan-id building-id level-id background-id]]
                   {:fx [[:dispatch [::plan-loaded plan-id]]
                         [:dispatch [:nav/update-query (fn [q]
                                                         (-> q
                                                             (assoc :background-id background-id
                                                                    :building-id   (str building-id)
                                                                    :level-id      (str level-id)
                                                                    :action        "set-scale")
                                                             (dissoc :route-id :geofence-id :unit-id
                                                                     :opening-id :locator-id)))]]]}))

(rf/reg-event-fx ::add-background-error
                 (fn [_ [_ error]]
                   (js/console.error "Failed to add background image:" error)
                   {:fx [[:dispatch [:notifications/notify-error {:message "Failed to add background image"}]]]}))

;; Once the process initiated, update background offset by dragging the mouse
(rf/reg-event-fx ::update-background-offset
                 (fn [{:keys [db]} [_ {:keys [background-id asset-id offset-px]}]]
                   (let [selected-building-id (-> db :quuppa.routing/match :parameters :query :building-id)
                         selected-level-id    (-> db :quuppa.routing/match :parameters :query :level-id)
                         site-plan-id         (or (-> db :quuppa.routing/match :parameters :path :plan-id) :new)
                         bg-path              [:site-plan site-plan-id :imdf :venue :buildings selected-building-id
                                               :levels selected-level-id :siteresource-backgroundimages background-id]]
                     (js/console.log "Start moving background image " offset-px)
                     {:db (assoc-in db (conj bg-path :offset-px) offset-px)
                      :fx [[:server-dispatch {:event      [:qsm/update-background-image-asset
                                                           {:asset-id   asset-id
                                                            :offset-px  offset-px}]
                                              :on-success :ignore
                                              :on-error   [::update-background-image-asset-error]}]]})))

(rf/reg-event-fx ::update-background-rotation
                 (fn [{:keys [db]} [_ {:keys [background-id asset-id rotation-deg]}]]
                   (let [selected-building-id (-> db :quuppa.routing/match :parameters :query :building-id)
                         selected-level-id    (-> db :quuppa.routing/match :parameters :query :level-id)
                         site-plan-id         (or (-> db :quuppa.routing/match :parameters :path :plan-id) :new)
                         bg-path              [:site-plan site-plan-id :imdf :venue :buildings selected-building-id
                                               :levels selected-level-id :siteresource-backgroundimages background-id]]
                     {:db (assoc-in db (conj bg-path :rotation-deg) rotation-deg)
                      :fx [[:server-dispatch {:event      [:qsm/update-background-image-asset
                                                           {:asset-id    asset-id
                                                            :rotation-deg rotation-deg}]
                                              :on-success :ignore
                                              :on-error   [::update-background-image-asset-error]}]]})))

(rf/reg-event-fx ::move-background-order
                 (fn [{:keys [db]} [_ {:keys [background-id direction plan-id building-id level-id]}]]
                   (let [bg-path  [:site-plan plan-id :imdf :venue :buildings building-id
                                   :levels level-id :siteresource-backgroundimages]
                         all-bgs  (get-in db bg-path)
                         sorted   (vec (sort-by (comp #(or (:sort-order %) 0) second) all-bgs))
                         ;; Assign stable sequential sort-orders so all images have distinct integers
                         normalised (into {} (map-indexed (fn [i [id asset]]
                                                            [id (assoc asset :sort-order i)])
                                                          sorted))
                         idx      (first (keep-indexed (fn [i [id _]] (when (= id background-id) i)) sorted))
                         swap-idx (if (= direction :up) (dec idx) (inc idx))]
                     (when (and idx (>= swap-idx 0) (< swap-idx (count sorted)))
                       (let [[id-a] (nth sorted idx)
                             [id-b] (nth sorted swap-idx)
                             order-a (get-in normalised [id-a :sort-order])
                             order-b (get-in normalised [id-b :sort-order])]
                         {:db (-> db
                                  (update-in bg-path merge normalised)   ; normalise all first
                                  (assoc-in (conj bg-path id-a :sort-order) order-b)
                                  (assoc-in (conj bg-path id-b :sort-order) order-a))
                          :fx [[:server-dispatch {:event      [:qsm/update-background-image-asset
                                                               {:asset-id (get-in normalised [id-a :asset-id])
                                                                :sort-order order-b}]
                                                  :on-success :ignore}]
                               [:server-dispatch {:event      [:qsm/update-background-image-asset
                                                               {:asset-id (get-in normalised [id-b :asset-id])
                                                                :sort-order order-a}]
                                                  :on-success :ignore}]]})))))


;; Add a new geofence to a level in the IMDF structure.
;; Sets geofence-id in URL so tree can select the new geofence.
;; initial-points should be provided based on the current image dimensions.
;; Automatically assigns a color based on the number of existing geofences.
(rf/reg-event-fx ::add-geofence
  (fn [{:keys [db]} [_ plan-id building-id level-id geofence-name initial-points]]
    (let [geofence-id (str (random-uuid))
          existing-geofences (get-in db [:site-plan plan-id :imdf :venue :buildings building-id :levels level-id :geofences])
          geofence-index (count existing-geofences)
          geofence-color (nth geofence-colors (mod geofence-index (count geofence-colors)))
          new-geofence {:id geofence-id
                        :name geofence-name
                        :points (or initial-points [])
                        :color geofence-color}]
      (js/console.log "EVENT add-geofence: geofence-id=" geofence-id "color=" geofence-color)
      {:db (assoc-in db [:site-plan plan-id :imdf :venue :buildings building-id :levels level-id :geofences geofence-id] new-geofence)
       :fx [[:dispatch [:nav/update-query merge {:geofence-id geofence-id}]]]})))

(rf/reg-event-fx ::update-geofence-points
  (fn [{:keys [db]} [_ plan-id building-id level-id geofence-id points]]
    {:db (assoc-in db [:site-plan plan-id :imdf :venue :buildings building-id :levels level-id :geofences geofence-id :points] points)}))

;; Toggle whether a geofence is used as a tracking area
;; When setting as tracking area, also set the level's geometry from the geofence
;; if the level doesn't already have one.
(rf/reg-event-fx ::toggle-geofence-tracking-area
  (fn [{:keys [db]} [_ plan-id building-id level-id geofence-id use-as-tracking-area]]
    (let [level-path [:site-plan plan-id :imdf :venue :buildings building-id :levels level-id]
          current-geometry (get-in db (conj level-path :geometry))
          geofence-points (get-in db (conj level-path :geofences geofence-id :points))]
      {:db (cond-> db
             true
             (assoc-in (conj level-path :geofences geofence-id :use-as-tracking-area) use-as-tracking-area)
             ;; Set level geometry from geofence if enabling tracking area and level has no geometry
             (and use-as-tracking-area (nil? current-geometry) (seq geofence-points))
             (assoc-in (conj level-path :geometry)
                       {:type "Polygon" :coordinates [geofence-points]}))})))

;; Distribute locators automatically within a tracking area geofence.
;; This will calculate optimal locator positions based on the geofence geometry
;; and the coverage area algorithm.
(rf/reg-event-fx ::distribute-locators
  (fn [{:keys [db]} [_ plan-id building-id level-id geofence-id]]
    (js/console.log "Distributing locators for geofence:" geofence-id)
    (let [;; Get selected background ID from routing parameters
          selected-background-id (-> db :quuppa.routing/match :parameters :query :background-id)
          ;; Get pixels-per-meter from the background image scale
          pixels-per-meter (get-in db [:site-plan plan-id :imdf :venue :buildings building-id :levels level-id :siteresource-backgroundimages selected-background-id :ppx] 100)]
      (js/console.log "distribute-locators - building-id:" building-id "level-id:" level-id "background-id:" selected-background-id "ppx:" pixels-per-meter)
      {:fx [[:server-dispatch {:event [:planner/distribute-locators
                                       {:plan-id plan-id
                                        :building-id building-id
                                        :level-id level-id
                                        :geofence-id geofence-id
                                        :pixels-per-meter pixels-per-meter
                                        :options {:area-type :semi-confined
                                                  :installation-height 3.0
                                                  :min-wall-distance 1.0
                                                  :grid-resolution 0.5  ;; Accurate grid for good coverage
                                                  :min-coverage 0.95}}]  ;; Target 95% coverage
                               :timeout (* 5 60 1000)  ;; 5 minute timeout for thorough coverage
                               :on-success [::locators-distributed plan-id building-id level-id]
                               :on-error [::distribute-locators-error]}]
            [:dispatch [:notifications/notify
                        {:type :info
                         :message "Calculating locator positions..."}]]]})))

(rf/reg-event-fx ::locators-distributed
  (fn [{:keys [db]} [_ plan-id building-id level-id result]]
    (js/console.log "Locators distributed:" result)
    ;; Check if the server returned an error in the result
    (if (:error result)
      {:fx [[:dispatch [:notifications/notify
                        {:type :error
                         :message (str "Failed to distribute locators: " (:error result))}]]]}
      (let [;; Backend returns meter coords; convert to pixels for rendering.
            selected-background-id (-> db :quuppa.routing/match :parameters :query :background-id)
            ppm (or (get-in db [:site-plan plan-id :imdf :venue :buildings building-id
                                :levels level-id :siteresource-backgroundimages selected-background-id :ppx])
                    100)
            ;; Image height for y-axis flip (meter convention: y=0 at bottom, growing up)
            img-height-px (or (get-in db [:site-plan plan-id :imdf :venue :buildings building-id
                                          :levels level-id :siteresource-backgroundimages selected-background-id :imageHeightPx])
                               (get-in db [:site-plan plan-id :imdf :venue :buildings building-id
                                           :levels level-id :siteresource-backgroundimages selected-background-id :image-height-px])
                               0)
            locators (:locators result)
            locators-count (:count result)
            coverage (:coverage result)]
        ;; Add locators to the level's locators collection
        {:db (assoc-in db [:site-plan plan-id :imdf :venue :buildings building-id :levels level-id :locators]
                       (into {}
                             (map-indexed (fn [idx locator]
                                            [(str (random-uuid))
                                             (assoc locator
                                                    :x (* (:x locator) ppm)
                                                    :y (if (pos? img-height-px)
                                                         (- img-height-px (* (:y locator) ppm))
                                                         (* (:y locator) ppm))
                                                    :name (str "loc" (gstr/format "%03d" (inc idx))))])
                                          locators)))
         :fx [[:dispatch [:notifications/notify
                          {:type :success
                           :message (str "Distributed " locators-count " locators with "
                                         (js/Math.round (* 100 (:average-coverage coverage))) "% average coverage")}]]]}))))

(rf/reg-event-fx ::distribute-locators-error
  (fn [_ [_ error]]
    (js/console.error "Locator distribution failed:" error)
    (let [error-msg (cond
                      (= :chsk/timeout (:error (:ex-data error)))
                      "Timed out - algorithm is still running on server"

                      (:err-msg error)
                      (:err-msg error)

                      (:status-text error)
                      (:status-text error)

                      :else
                      "Unknown error")]
      {:fx [[:dispatch [:notifications/notify
                        {:type :error
                         :message (str "Failed to distribute locators: " error-msg)}]]]})))

;; Delete a geofence from a level in the IMDF structure.
(rf/reg-event-fx ::delete-geofence
  (fn [{:keys [db]} [_ plan-id building-id level-id geofence-id]]
    {:db (update-in db [:site-plan plan-id :imdf :venue :buildings building-id :levels level-id :geofences] dissoc geofence-id)
     :fx [[:dispatch [:nav/update-query dissoc :geofence-id]]]}))

;; Add a new unit to a level in the IMDF structure.
;; Sets unit-id in URL so tree can select the new unit.
;; initial-points should be provided based on the current image dimensions.
;; Automatically assigns a color based on the number of existing units.
(rf/reg-event-fx ::add-unit
  (fn [{:keys [db]} [_ plan-id building-id level-id unit-name initial-points]]
    (let [unit-id (str (random-uuid))
          existing-units (get-in db [:site-plan plan-id :imdf :venue :buildings building-id :levels level-id :units])
          unit-index (count existing-units)
          unit-color (nth unit-colors (mod unit-index (count unit-colors)))
          new-unit {:id unit-id
                    :name unit-name
                    :points (or initial-points [])
                    :color unit-color
                    :category "room"}]
      (js/console.log "EVENT add-unit: unit-id=" unit-id "color=" unit-color)
      {:db (assoc-in db [:site-plan plan-id :imdf :venue :buildings building-id :levels level-id :units unit-id] new-unit)
       :fx [[:dispatch [:nav/update-query merge {:unit-id unit-id}]]]})))

(rf/reg-event-fx ::update-unit-points
  (fn [{:keys [db]} [_ plan-id building-id level-id unit-id points]]
    {:db (assoc-in db [:site-plan plan-id :imdf :venue :buildings building-id :levels level-id :units unit-id :points] points)}))

;; Delete a unit from a level in the IMDF structure.
(rf/reg-event-fx ::delete-unit
  (fn [{:keys [db]} [_ plan-id building-id level-id unit-id]]
    {:db (update-in db [:site-plan plan-id :imdf :venue :buildings building-id :levels level-id :units] dissoc unit-id)
     :fx [[:dispatch [:nav/update-query dissoc :unit-id]]]}))

;; Auto-detect units and openings for a background image
;; If background already has :autodetected true, first removes all autodetected units, openings, and geofences
(rf/reg-event-fx ::auto-detect-units-openings
  (fn [{:keys [db]} [_ plan-id building-id level-id background-id]]
    (let [background (get-in db [:site-plan plan-id :imdf :venue :buildings building-id :levels level-id :siteresource-backgroundimages background-id])
          is-redetect (:autodetected background)
          pixels-per-meter (or (:ppx background) 100)
          asset-id (:asset-id background)
          ;; Remove all autodetected units, openings, and geofences if re-detecting
          db-after-cleanup (if is-redetect
                             (-> db
                                 (update-in [:site-plan plan-id :imdf :venue :buildings building-id :levels level-id :units]
                                            (fn [units]
                                              (into {} (remove (fn [[_ unit]] (:autodetected unit)) units))))
                                 (update-in [:site-plan plan-id :imdf :venue :buildings building-id :levels level-id :openings]
                                            (fn [openings]
                                              (into {} (remove (fn [[_ opening]] (:autodetected opening)) openings))))
                                 (update-in [:site-plan plan-id :imdf :venue :buildings building-id :levels level-id :geofences]
                                            (fn [geofences]
                                              (into {} (remove (fn [[_ geofence]] (:autodetected geofence)) geofences))))
                                 ;; Also clear existing level contour
                                 (update-in [:site-plan plan-id :imdf :venue :buildings building-id :levels level-id]
                                            dissoc :level-contour)
                                 ;; Clear derived relationships so stale ones don't persist
                                 (assoc-in [:site-plan plan-id :imdf :relationships] []))
                             db)]
      (js/console.log "auto-detect-units-openings - background:" (clj->js background))
      (js/console.log "auto-detect-units-openings - ppx:" pixels-per-meter "asset-id:" asset-id)
      (if-not asset-id
        ;; No asset ID - can't detect
        {:db db
         :fx [[:dispatch [:notifications/notify
                          {:type :error
                           :message "Cannot auto-detect: No background image asset found"}]]]}
        ;; Fetch the image and send to detection service
        {:db db-after-cleanup
         :fx [[:dispatch [:notifications/notify
                          {:type :info
                           :message (if is-redetect
                                      "Re-detecting units and openings..."
                                      "Auto-detecting units and openings...")}]]
              [:http-xhrio {:method :post
                            :uri (str "/planner/auto-detect?asset-id=" asset-id "&pixels-per-meter=" pixels-per-meter "&min-room-area-m2=6.0")
                            :format (ajax/json-request-format)
                            :params {}
                            :response-format (ajax/json-response-format {:keywords? true})
                            :on-success [::auto-detect-success plan-id building-id level-id background-id]
                            :on-failure [::auto-detect-failure]}]]}))))

;; Handle successful detection response
(rf/reg-event-fx ::auto-detect-success
  (fn [{:keys [db]} [_ plan-id building-id level-id background-id response]]
    (let [;; The backend returns coordinates in meters with y=0 at top (image convention).
          ;; We flip y when converting back to pixels so that on save the meter coords
          ;; have y=0 at bottom (growing up), matching the stored coordinate convention.
          ppm (or (get-in db [:site-plan plan-id :imdf :venue :buildings building-id
                              :levels level-id :siteresource-backgroundimages background-id :ppx])
                  100)
          ;; Image height in pixels from detection service response metadata
          img-height-px (or (get-in response [:metadata :height]) 0)
          pt->px  (fn [[x y]] [(* x ppm) (if (pos? img-height-px) (- img-height-px (* y ppm)) (* y ppm))])
          pts->px (fn [pts] (when (seq pts) (mapv pt->px pts)))

          rooms (:rooms response)
          openings (or (:openings response) [])
          footprint (:footprint response)
          ;; Convert footprint points from meters to pixels
          level-contour (pts->px (:points footprint))         ;; building outline in pixels
          tracking-area-points (pts->px (:tracking_area footprint))  ;; bounding box in pixels
          ;; Convert rooms to units (meters → pixels)
          relationships (:relationships response)
          new-units (into {}
                          (map-indexed
                            (fn [idx room]
                              (let [unit-id (str (:id room))
                                    unit-name (or (:name room) (str "unit" (gstr/format "%03d" (inc idx))))
                                    points (pts->px (:points room))
                                    color (nth unit-colors (mod idx (count unit-colors)))]
                                [unit-id {:id unit-id
                                          :name unit-name
                                          :points points
                                          :color color
                                          :category (:category room)
                                          :autodetected true
                                          :area (* (or (:area room) 0) ppm ppm)
                                          :centroid (when-let [c (:centroid room)] (pt->px c))}]))
                            rooms))
          ;; Convert openings (meters → pixels)
          new-openings (into {}
                             (map-indexed
                               (fn [idx opening]
                                 (let [opening-id (str (:id opening))
                                       opening-type (:type opening)
                                       opening-name (str (if (= opening-type "door_swing") "door" "opening")
                                                         (gstr/format "%03d" (inc idx)))]
                                   [opening-id {:id opening-id
                                                :name opening-name
                                                :type opening-type
                                                :center (when-let [c (:center opening)] (pt->px c))
                                                :radius (* (or (:radius opening) 0) ppm)
                                                :points (pts->px (:points opening))
                                                :autodetected true}]))
                               openings))
          ;; Create tracking area geofence from bounding box if available
          new-geofence (when (seq tracking-area-points)
                         (let [geofence-id (str (random-uuid))]
                           {geofence-id {:id geofence-id
                                         :name "Tracking Area"
                                         :points tracking-area-points
                                         :color "#377eb8"
                                         :use-as-tracking-area true
                                         :autodetected true}}))]
      {:db (-> db
               ;; Add units
               (update-in [:site-plan plan-id :imdf :venue :buildings building-id :levels level-id :units]
                          (fn [existing-units]
                            (merge (or existing-units {}) new-units)))
               ;; Add openings
               (update-in [:site-plan plan-id :imdf :venue :buildings building-id :levels level-id :openings]
                          (fn [existing-openings]
                            (merge (or existing-openings {}) new-openings)))
               ;; Add tracking area geofence if created
               (cond-> new-geofence
                 (update-in [:site-plan plan-id :imdf :venue :buildings building-id :levels level-id :geofences]
                            (fn [existing-geofences]
                              (merge (or existing-geofences {}) new-geofence))))
               ;; Store level contour if available
               (cond-> (seq level-contour)
                 (assoc-in [:site-plan plan-id :imdf :venue :buildings building-id :levels level-id :level-contour] level-contour))
               ;; Set level geometry from the best available source:
               ;; prefer footprint (level contour), fall back to tracking area bounding box
               (cond->
                 (seq level-contour)
                 (assoc-in [:site-plan plan-id :imdf :venue :buildings building-id :levels level-id :geometry]
                           {:type "Polygon" :coordinates [level-contour]})
                 (and (not (seq level-contour)) (seq tracking-area-points))
                 (assoc-in [:site-plan plan-id :imdf :venue :buildings building-id :levels level-id :geometry]
                           {:type "Polygon" :coordinates [tracking-area-points]}))
               ;; Mark background as autodetected
               (assoc-in [:site-plan plan-id :imdf :venue :buildings building-id :levels level-id :siteresource-backgroundimages background-id :autodetected] true)
               ;; Store derived relationships (referenced by the backend-assigned unit/opening UUIDs)
               (assoc-in [:site-plan plan-id :imdf :relationships] (or relationships [])))
       :fx [[:dispatch [:notifications/notify
                        {:type :success
                         :message (str "Auto-detected " (count new-units) " units and " (count new-openings) " openings"
                                       (when new-geofence " + tracking area"))}]]]})))

;; Handle detection failure
(rf/reg-event-fx ::auto-detect-failure
  (fn [_ [_ error]]
    (js/console.error "Auto-detect failed:" error)
    {:fx [[:dispatch [:notifications/notify
                      {:type :error
                       :message (str "Auto-detection failed: " (or (:status-text error) "Unknown error"))}]]]}))

(rf/reg-event-fx ::save-plan
  (fn [{:keys [db]} [_ plan-id]]
    (let [plan (get-in db [:site-plan plan-id])]
      (if (= :planner-new-plan (-> db :quuppa.routing/match :data :name))
        {:fx [[:server-dispatch {:event      [:planner/create-plan (select-keys plan [:nodes])]
                                 :on-success [::plan-created]
                                 :on-error   [::save-plan-error]}]]}
        {:fx [[:server-dispatch {:event      [:planner/save-plan {:id plan-id
                                                                  :imdf (:imdf plan)}]
                                 :on-success [::plan-saved]
                                 :on-error   [::save-plan-error]}]
              [:dispatch [:notifications/notify
                          {:type :info
                           :message "Saving plan..."}]]]}))))

(rf/reg-event-fx ::plan-saved
  (fn [_ [_ _]]
    {:fx [[:dispatch [:notifications/notify
                      {:type :success
                       :message "Plan saved successfully"}]]]}))

(rf/reg-event-fx ::plan-created
                 (fn [{:keys [db]} [_ result]]
                   (let [new-plan-id (str (:id result))
                         ;; Copy the locally-built plan data from :new to the real plan-id
                         ;; so any locally-added IMDF (buildings, levels, backgrounds) is preserved
                         existing-new-plan (get-in db [:site-plan :new])]
                     (js/console.log "plan-created: new-plan-id=" new-plan-id "result=" (clj->js result))
                     {:db (cond-> db
                            existing-new-plan
                            (assoc-in [:site-plan new-plan-id] existing-new-plan))
                      :fx [[:dispatch [:notifications/notify
                                       {:type :success
                                        :message "Plan created successfully"}]]
                           [:dispatch [:nav/push-state :planner-site-plan {:plan-id new-plan-id}]]]})))

(rf/reg-event-fx ::save-plan-error
                 (fn [_ [_ error]]
                   (js/console.error "Save plan failed:" error)
                   {:fx [[:dispatch [:notifications/notify
                                     {:type :error
                                      :message (str "Failed to save plan: " (or (:status-text error) "Unknown error"))}]]]}))
